import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useLocations } from '../hooks/use-locations';
import { useSiteInfo } from '@/SiteInfo/hooks/use-site-info';
import { useUserLocation } from '../hooks/use-user-location';
import { formatLocationHours, getStoreStatus } from '../lib/format-hours';
import { isCurrentlyActive } from '@/lib/date-utils';
import { Location, LocationAd, LocationService, RichTextBlock } from '../types/locations';
import { AdViewerSheet } from '@/Ads/components/AdViewerSheet';
import { useNotifications } from '@/Notifications/hooks/use-notifications';
import type { Notification } from '@/Notifications/types/notifications';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { SEO } from '@/components/SEO';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { geocodeAddress, batchGeocodeAddresses } from '@/lib/geocode-cache';
import { BASEMAP_ATTRIBUTION, BASEMAP_MAX_ZOOM, BASEMAP_TILE_URL } from '@/lib/basemap';
import { RichTextRenderer } from '@/components/RichTextRenderer';

import { renderToStaticMarkup } from 'react-dom/server';
import { IconAlertCircle, IconBellExclamation, IconBrandFacebook, IconBrandInstagram, IconBrandLinkedin, IconBrandPinterest, IconBrandTiktok, IconBrandX, IconBrandYoutube, IconCheck, IconClock, IconExternalLink, IconFileText, IconInfoCircle, IconMail, IconMapPin, IconMapPinFilled, IconPhone, IconShoppingCart, IconTag, IconX, type TablerIcon } from '@tabler/icons-react';

const DEFAULT_CENTER: [number, number] = [47.6062, -122.3321];

// Helper to extract text from rich text children
const extractHeadingText = (block: RichTextBlock): string => {
  return block.children
    .map((child) => {
      if ('text' in child) {
        return (child as { text: string }).text;
      }
      return '';
    })
    .join('');
};

// Parse rich text content into sections split by headings
interface ContentSection {
  title: string;
  content: RichTextBlock[];
}

const parseContentSections = (content: RichTextBlock[] | string | undefined): ContentSection[] => {
  if (!content || typeof content === 'string' || !Array.isArray(content)) {
    return [];
  }

  const sections: ContentSection[] = [];
  let currentSection: ContentSection | null = null;

  for (const block of content) {
    if (block.type === 'heading') {
      // Start a new section
      if (currentSection) {
        sections.push(currentSection);
      }
      currentSection = {
        title: extractHeadingText(block),
        content: [], // Don't include the heading itself in content - it's the tab title
      };
    } else if (currentSection) {
      // Add content to current section
      currentSection.content.push(block);
    } else {
      // Content before any heading - create an "Overview" section
      if (sections.length === 0 && !currentSection) {
        currentSection = {
          title: 'Overview',
          content: [block],
        };
      }
    }
  }

  // Push the last section
  if (currentSection) {
    sections.push(currentSection);
  }

  return sections;
};

// Render the Tabler pin to static markup for the Leaflet divIcon
const getMarkerSvg = (isHighlighted = false) => {
  const size = isHighlighted ? 40 : 32;
  return renderToStaticMarkup(
    <IconMapPinFilled
      size={size}
      style={{ color: 'hsl(var(--primary))', fill: 'currentColor' }}
    />,
  );
};

const getLinkInfo = (url: string): { icon: TablerIcon; label: string } => {
  const lowerUrl = url.toLowerCase();

  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.com')) {
    return { icon: IconBrandFacebook, label: 'Facebook' };
  }
  if (lowerUrl.includes('instagram.com')) {
    return { icon: IconBrandInstagram, label: 'Instagram' };
  }
  if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) {
    return { icon: IconBrandX, label: 'X / Twitter' };
  }
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    return { icon: IconBrandYoutube, label: 'YouTube' };
  }
  if (lowerUrl.includes('linkedin.com')) {
    return { icon: IconBrandLinkedin, label: 'LinkedIn' };
  }
  if (lowerUrl.includes('tiktok.com')) {
    return { icon: IconBrandTiktok, label: 'TikTok' };
  }
  if (lowerUrl.includes('pinterest.com')) {
    return { icon: IconBrandPinterest, label: 'Pinterest' };
  }
  if (lowerUrl.includes('mailto:')) {
    return { icon: IconMail, label: 'Email' };
  }
  if (lowerUrl.includes('shop') || lowerUrl.includes('instacart.com') || lowerUrl.includes('egrowcery.com')|| lowerUrl.includes('doordash.com') || lowerUrl.includes('store') || lowerUrl.includes('cart') || lowerUrl.includes('order')) {
    return { icon: IconShoppingCart, label: 'Shop Online' };
  }

  return { icon: IconExternalLink, label: 'Visit Site' };
};

const createCustomIcon = (isHighlighted = false) => {
  const size = isHighlighted ? 40 : 32;
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="marker-container" style="${isHighlighted ? 'filter: drop-shadow(0 0 8px hsl(var(--primary)));' : ''}">
        ${getMarkerSvg(isHighlighted)}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

interface GeocodedLocation {
  location: Location;
  coords: [number, number];
}

const FitBoundsToMarkers = ({ coordinates }: { coordinates: [number, number][] }) => {
  const map = useMap();

  useEffect(() => {
    if (coordinates.length === 0) return;

    if (coordinates.length === 1) {
      map.setView(coordinates[0], 13);
    } else {
      const bounds = L.latLngBounds(coordinates.map(c => L.latLng(c[0], c[1])));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, coordinates]);

  return null;
};

const AllLocationsMap = ({
  geocodedLocations,
  highlightedLocation,
  onMarkerClick,
  isLoading
}: {
  geocodedLocations: GeocodedLocation[];
  highlightedLocation: string | null;
  onMarkerClick: (locationTitle: string) => void;
  isLoading: boolean;
}) => {
  const coordinates = geocodedLocations.map(gl => gl.coords);
  const center: [number, number] = coordinates.length > 0
    ? coordinates[0]
    : DEFAULT_CENTER;

  if (isLoading) {
    return <Skeleton className="h-[300px] md:h-[400px] w-full rounded-2xl" />;
  }

  if (geocodedLocations.length === 0) {
    return null;
  }

  return (
    <Card className="overflow-hidden rounded-2xl border shadow-sm mb-8 relative z-0">
      <div className="h-[300px] md:h-[400px] w-full relative [&_.leaflet-pane]:z-[1] [&_.leaflet-control]:z-[2]">
        <MapContainer
          center={center}
          zoom={10}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
          className="rounded-2xl"
        >
          <TileLayer
            attribution={BASEMAP_ATTRIBUTION}
            url={BASEMAP_TILE_URL}
            maxZoom={BASEMAP_MAX_ZOOM}
          />
          <FitBoundsToMarkers coordinates={coordinates} />
          {geocodedLocations.map((gl, idx) => {
            const isHighlighted = highlightedLocation === gl.location.LocationTitle;
            return (
              <Marker
                key={idx}
                position={gl.coords}
                icon={createCustomIcon(isHighlighted)}
                eventHandlers={{
                  click: () => onMarkerClick(gl.location.LocationTitle),
                }}
              >
                <Popup className="custom-popup" closeButton={true}>
                  <div className="text-sm space-y-1 min-w-[180px]">
                    <p className="font-semibold text-foreground">{gl.location.LocationTitle}</p>
                    <p className="text-muted-foreground text-xs">{gl.location.LocationAddress.LocationAddressLineOne}</p>
                    <p className="text-muted-foreground text-xs">
                      {gl.location.LocationAddress.LocationAddressCity}, {gl.location.LocationAddress.LocationAddressState}
                    </p>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </Card>
  );
};

const LocationDetailsDialog = ({ location, open, onOpenChange }: { location: Location | null; open: boolean; onOpenChange: (open: boolean) => void }) => {
  const [adViewerOpen, setAdViewerOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState<LocationAd | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [serviceSheetOpen, setServiceSheetOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<LocationService | null>(null);

  useEffect(() => {
    if (!location || !open) return;

    const addressString = [
      location.LocationAddress.LocationAddressLineOne,
      location.LocationAddress.LocationAddressCity,
      location.LocationAddress.LocationAddressState,
      location.LocationAddress.LocationAddressZipCode,
    ].filter(Boolean).join(', ');

    if (!addressString) return;

    setIsGeocoding(true);
    geocodeAddress(addressString)
      .then(coords => {
        if (coords) {
          setMapCenter(coords);
        }
      })
      .catch(error => {
        console.error('Geocoding error:', error);
      })
      .finally(() => {
        setIsGeocoding(false);
      });
  }, [location, open]);

  if (!location) return null;

  const formattedHours = formatLocationHours(location.LocationHours);
  const fullAddress = [
    location.LocationAddress.LocationAddressLineOne,
    location.LocationAddress.LocationAddressLineTwo,
    `${location.LocationAddress.LocationAddressCity}, ${location.LocationAddress.LocationAddressState} ${location.LocationAddress.LocationAddressZipCode}`,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-2xl">{location.LocationTitle}</DialogTitle>
          {location.LocationBanner && (
            <DialogDescription className="text-base !mt-0">{location.LocationBanner}</DialogDescription>
          )}
        </DialogHeader>

        {/* Map */}
        <Card className="overflow-hidden rounded-lg border shadow-sm">
          {isGeocoding ? (
            <Skeleton className="h-[200px] w-full" />
          ) : (
            <div className="h-[200px] w-full relative">
              <MapContainer
                key={`${mapCenter[0]}-${mapCenter[1]}`}
                center={mapCenter}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
                zoomControl={true}
                className="rounded-lg"
              >
                <TileLayer
                  attribution={BASEMAP_ATTRIBUTION}
                  url={BASEMAP_TILE_URL}
                  maxZoom={BASEMAP_MAX_ZOOM}
                />
                <Marker position={mapCenter} icon={createCustomIcon()}>
                  <Popup className="custom-popup" closeButton={true}>
                    <div className="text-sm space-y-1 min-w-[180px]">
                      <p className="font-semibold text-foreground">{location.LocationTitle}</p>
                      <p className="text-muted-foreground text-xs">{location.LocationAddress.LocationAddressLineOne}</p>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          )}
        </Card>

        <div className="space-y-6 mt-4">
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <IconMapPin className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium">Address</p>
                <p className="text-sm text-muted-foreground">{fullAddress}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <IconPhone className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium">Phone</p>
                <p className="text-sm text-muted-foreground">{location.LocationPhoneNumber}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <IconClock className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium">Hours</p>
                <p className="text-sm text-muted-foreground whitespace-pre-line">{formattedHours}</p>
              </div>
            </div>
          </div>

          {location.LocationCoupons && (
            <div>
              <Badge className="mb-2 bg-emerald-500 hover:bg-emerald-600 text-white">
                <IconTag className="h-3 w-3 mr-1" />
                Coupons Available
              </Badge>
            </div>
          )}

          {location.ads && location.ads.filter(ad => isCurrentlyActive(ad.AdStartDate, ad.AdEndDate)).length > 0 && (
            <div>
              <p className="text-sm font-medium mb-3">Store Ads</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {location.ads
                  .filter(ad => isCurrentlyActive(ad.AdStartDate, ad.AdEndDate))
                  .map((ad, idx) => (
                  <Button
                    key={idx}
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setSelectedAd(ad);
                      setAdViewerOpen(true);
                    }}
                    disabled={!ad.AdFile?.url}
                  >
                    <IconFileText className="h-4 w-4 mr-2" />
                    {ad.ad_type?.AdType || 'View Ad'}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {location.LocationServices && location.LocationServices.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-3">Services</p>
              <div className="flex flex-wrap gap-2">
                {location.LocationServices.map((service, idx) => (
                  <Badge
                    key={idx}
                    variant="default"
                    className={`bg-primary text-primary-foreground ${service.LocationServiceShowMore ? 'cursor-pointer hover:bg-primary/90' : ''}`}
                    onClick={() => {
                      if (service.LocationServiceShowMore) {
                        setSelectedService(service);
                        setServiceSheetOpen(true);
                      }
                    }}
                  >
                    {service.LocationServiceShowMore && <IconInfoCircle className="h-3 w-3 mr-1" />}
                    {service.LocationServiceDepartment}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {location.LocationLinks && location.LocationLinks.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-3">Links</p>
              <div className="flex flex-wrap gap-2">
                {location.LocationLinks.map((link, idx) => {
                  const linkInfo = getLinkInfo(link.LocationLink);
                  return (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      asChild
                    >
                      <a
                        href={link.LocationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <linkInfo.icon className="h-4 w-4 mr-2" />
                        {linkInfo.label}
                      </a>
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

              </DialogContent>
      </Dialog>

      <AdViewerSheet
        open={adViewerOpen}
        onOpenChange={setAdViewerOpen}
        ad={selectedAd}
        locationTitle={location.LocationTitle}
        locationBanner={location.LocationBanner}
      />

      <Sheet open={serviceSheetOpen} onOpenChange={setServiceSheetOpen}>
        <SheetContent side="right" className="z-[100] w-full sm:w-[90vw] sm:max-w-2xl p-0 flex flex-col">
          <SheetHeader className="px-6 pt-6 pb-4 border-b text-left">
            <SheetTitle className="text-2xl">{selectedService?.LocationServiceDepartment}</SheetTitle>
          </SheetHeader>

          {(() => {
            const sections = parseContentSections(selectedService?.LocationServiceDetails);
            const hasMultipleSections = sections.length > 1;

            return (
              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                {/* Service Image */}
                {selectedService?.LocationServiceImage?.url && (
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={selectedService.LocationServiceImage.url}
                      alt={selectedService.LocationServiceImage.name || selectedService.LocationServiceDepartment}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                )}

                {/* Service Details - Tabbed if multiple sections, simple if single */}
                {selectedService?.LocationServiceDetails && (
                  hasMultipleSections ? (
                    <Tabs defaultValue={sections[0]?.title} className="w-full">
                      <TabsList className="w-full flex-wrap h-auto gap-1 bg-muted/50 p-1">
                        {sections.map((section, idx) => (
                          <TabsTrigger
                            key={idx}
                            value={section.title}
                            className="flex-1 min-w-fit text-xs sm:text-sm"
                          >
                            {section.title}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      {sections.map((section, idx) => (
                        <TabsContent key={idx} value={section.title} className="mt-4">
                          <div className="prose prose-sm max-w-none">
                            <RichTextRenderer content={section.content} />
                          </div>
                        </TabsContent>
                      ))}
                    </Tabs>
                  ) : (
                    <div className="prose prose-sm max-w-none">
                      <RichTextRenderer content={selectedService.LocationServiceDetails} />
                    </div>
                  )
                )}
              </div>
            );
          })()}
        </SheetContent>
      </Sheet>
    </>
  );
};

const Locations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: locations, isLoading, error } = useLocations();
  const { data: siteInfo } = useSiteInfo();
  const { data: notifications } = useNotifications();
  const { setLocation, isSelected, clearLocation } = useUserLocation();
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toastNotification, setToastNotification] = useState<Notification | null>(null);
  const [geocodedLocations, setGeocodedLocations] = useState<GeocodedLocation[]>([]);
  const [isGeocodingAll, setIsGeocodingAll] = useState(false);
  const [highlightedLocation, setHighlightedLocation] = useState<string | null>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Handle URL param to open location details modal
  useEffect(() => {
    const openParam = searchParams.get('open');
    if (openParam && locations) {
      const location = locations.find(loc => loc.LocationTitle === openParam);
      if (location) {
        setSelectedLocation(location);
        setDialogOpen(true);
        // Clear the URL param after opening
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, locations, setSearchParams]);

  // Geocode all locations when they load (with caching for performance)
  useEffect(() => {
    if (!locations || locations.length === 0) return;

    const geocodeLocations = async () => {
      setIsGeocodingAll(true);

      // Build address strings for all locations
      const addressMap = new Map<string, Location>();
      for (const location of locations) {
        const addressString = [
          location.LocationAddress.LocationAddressLineOne,
          location.LocationAddress.LocationAddressCity,
          location.LocationAddress.LocationAddressState,
          location.LocationAddress.LocationAddressZipCode,
        ].filter(Boolean).join(', ');

        if (addressString) {
          addressMap.set(addressString, location);
        }
      }

      // Batch geocode with caching (parallel requests)
      const geocodedMap = await batchGeocodeAddresses(Array.from(addressMap.keys()));

      // Build results
      const results: GeocodedLocation[] = [];
      for (const [address, location] of addressMap) {
        const coords = geocodedMap.get(address);
        if (coords) {
          results.push({ location, coords });
        }
      }

      setGeocodedLocations(results);
      setIsGeocodingAll(false);
    };

    geocodeLocations();
  }, [locations]);

  // Handle marker click - highlight and scroll to card
  const handleMarkerClick = useCallback((locationTitle: string) => {
    setHighlightedLocation(locationTitle);

    // Scroll to the card
    const cardElement = cardRefs.current.get(locationTitle);
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Clear highlight after a few seconds
    setTimeout(() => {
      setHighlightedLocation(null);
    }, 3000);
  }, []);

  // Get notifications for a specific location
  const getLocationNotifications = (locationTitle: string): Notification[] => {
    if (!notifications) return [];
    return notifications.filter((n) => {
      // Include global notifications (no locations) and location-specific ones
      if (!n.locations || n.locations.length === 0) return true;
      return n.locations.some((loc) => loc.LocationTitle === locationTitle);
    });
  };

  const handleNotificationClick = (locationTitle: string) => {
    const locationNotifications = getLocationNotifications(locationTitle);
    if (locationNotifications.length > 0) {
      setToastNotification(locationNotifications[0]);
    }
  };

  const handleViewDetails = (location: Location) => {
    setSelectedLocation(location);
    setDialogOpen(true);
  };

  const handleSelectLocation = (locationTitle: string) => {
    setLocation(locationTitle);
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <SEO
        title="Find a Location Near You | Local Grocery Shopping"
        description="Find a store near you. View store hours, directions, contact information, and services available at each location."
      />
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">{siteInfo?.SiteTitle} Locations</h1>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <IconAlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load locations. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        {/* All Locations Map - hidden on mobile */}
        {!error && (
          <div className="hidden md:block">
            <AllLocationsMap
              geocodedLocations={geocodedLocations}
              highlightedLocation={highlightedLocation}
              onMarkerClick={handleMarkerClick}
              isLoading={isLoading || isGeocodingAll}
            />
          </div>
        )}

        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2].map((i) => (
              <Card key={i} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <Skeleton className="h-6 w-32 mb-2" />
                  <Skeleton className="h-8 w-48" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-10 w-full mt-4" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!isLoading && !error && locations && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...locations].sort((a, b) => a.LocationTitle.localeCompare(b.LocationTitle)).map((location, index) => {
              const storeStatus = getStoreStatus(location.LocationHours);
              const fullAddress = [
                location.LocationAddress.LocationAddressLineOne,
                location.LocationAddress.LocationAddressLineTwo,
                `${location.LocationAddress.LocationAddressCity}, ${location.LocationAddress.LocationAddressState} ${location.LocationAddress.LocationAddressZipCode}`,
              ]
                .filter(Boolean)
                .join(', ');

              const isLocationSelected = isSelected(location.LocationTitle);
              const locationNotifications = getLocationNotifications(location.LocationTitle);
              const hasNotifications = locationNotifications.length > 0;

              const isHighlighted = highlightedLocation === location.LocationTitle;

              return (
                <Card
                  key={index}
                  ref={(el) => {
                    if (el) cardRefs.current.set(location.LocationTitle, el);
                  }}
                  className={`relative hover:shadow-lg transition-all duration-300 ${isLocationSelected ? 'ring-2 ring-primary' : ''} ${isHighlighted ? 'ring-2 ring-primary shadow-lg scale-[1.02]' : ''}`}
                >
                  {/* Notification Badge */}
                  {hasNotifications && (
                    <Badge
                      className="absolute -top-2 -right-2 bg-amber-500 hover:bg-amber-600 text-white cursor-pointer z-10 p-1.5"
                      onClick={() => handleNotificationClick(location.LocationTitle)}
                    >
                      <IconBellExclamation className="h-4 w-4" />
                    </Badge>
                  )}
                  <CardHeader>
                    <Badge
                      className={`w-fit mb-2 ${storeStatus.isOpen ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-rose-600 hover:bg-rose-700 text-white'}`}
                    >
                      <IconClock className="h-3 w-3 mr-1" />
                      {storeStatus.message}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-2xl">{location.LocationTitle}</CardTitle>
                      {isLocationSelected && (
                        <Badge variant="default" className="w-fit">
                          <IconCheck className="h-3 w-3 mr-1" />
                          Selected
                        </Badge>
                      )}
                    </div>
                    {location.LocationBanner && (
                      <p className="text-sm text-muted-foreground -mt-1">{location.LocationBanner}</p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <IconMapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <span>{fullAddress}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <IconPhone className="h-5 w-5 flex-shrink-0" />
                      <span>{location.LocationPhoneNumber}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant={isLocationSelected ? "destructive" : "default"}
                        className="flex-1"
                        onClick={() => isLocationSelected ? clearLocation() : handleSelectLocation(location.LocationTitle)}
                      >
                        {isLocationSelected ? (
                          <>Clear Selection</>
                        ) : (
                          <>Set as My Store</>
                        )}
                      </Button>
                      <Button 
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleViewDetails(location)}
                      >
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {!isLoading && !error && locations && locations.length === 0 && (
          <Alert>
            <IconAlertCircle className="h-4 w-4" />
            <AlertTitle>No Locations Found</AlertTitle>
            <AlertDescription>
              No locations are currently available. Please check back later.
            </AlertDescription>
          </Alert>
        )}

        <LocationDetailsDialog
          location={selectedLocation}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />

        {/* Toast Notification */}
        {toastNotification && (
          <div className="fixed top-4 left-4 right-4 md:left-auto md:right-4 z-50 max-w-sm mx-auto md:mx-0 animate-in slide-in-from-top-2 fade-in duration-300">
            <div className="bg-white rounded-lg shadow-lg border border-border overflow-hidden">
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10 flex-shrink-0 w-9 h-9">
                    <IconBellExclamation className="h-5 w-5 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-foreground">{toastNotification.NotificationTitle}</h4>
                    {toastNotification.NotificationDetails && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {toastNotification.NotificationDetails}
                      </p>
                    )}
                    {toastNotification.NotificationHasURL && toastNotification.NotificationURL && (
                      <a
                        href={toastNotification.NotificationURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline mt-2"
                      >
                        {toastNotification.NotificationButtonText || 'Learn more'}
                        <IconExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <button
                    onClick={() => setToastNotification(null)}
                    className="p-1 rounded-md hover:bg-muted flex-shrink-0"
                  >
                    <IconX className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Locations;





